import useResponsive from "@/app/hooks/useResponsive";
import DisplaySize from "@/app/utils/DisplaySize";
import { BASE_URL } from "@/constant/apiPath";
import { useLanguageContext } from "@/context/LanguageContext";
import { formatThousands } from "@/helpers";
import useMyQuery from "@/hooks/useMyQuery";
import type { FeedItem, FeedResponse } from "@/app/models/feed";
import { Skeleton, Typography } from "antd";
import classNames from "classnames";
import React from "react";
import PriceChange from "../PriceChange";
import { useCurrencyContext } from "@/context/CurrencyContext";
import circleIcon from "@/asset/images/icon_circle_1.svg";
import Image from "next/image";

const PAGE_SIZE = 10;

function FeedCarousel() {
  const screenSize = useResponsive();
  const { data, isLoading } = useMyQuery<FeedResponse>({
    url: "v1/market-movement/market/news-feed",
    params: {
      page: 1,
      limit: PAGE_SIZE,
    },
    includeParamsInQueryKey: true,
  });
  const { language } = useLanguageContext();
  const { currentCurrency } = useCurrencyContext();

  if (isLoading) {
    return (
      <div className="flex gap-2 p-1 overflow-hidden">
        {[...Array(10)].map((_, index) => (
          <div key={index} className="flex items-center gap-2 py-1">
            <Skeleton.Button active size="small" style={{ width: 60 }} />
            <Skeleton.Button active size="small" style={{ width: 80 }} />
            <Skeleton.Button active size="small" style={{ width: 40 }} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="w-full overflow-hidden relative">
      <div className="flex items-center gap-2 justify-between">
        <div className="flex items-center gap-1 min-w-[150px]">
          <Image
            src={circleIcon}
            alt="icon"
            width={16}
            height={16}
            style={{ marginRight: 8, display: "inline-block", verticalAlign: "middle" }}
          />
          <Typography.Text>BREAKING FEEDS:</Typography.Text>
        </div>
        <div className={`${screenSize < DisplaySize.Tablet ? "w-[85vw]" : "w-[90vw]"} overflow-hidden`}>
          <div className="whitespace-nowrap inline-block marquee">
            {/* First set of items */}
            {data?.data?.result?.map((item: FeedItem, index: number, array: FeedItem[]) => (
              <div
                key={`${item.id}-1`}
                className={"inline-flex items-center gap-1 rounded-md px-3 py-1 mx-1 cursor-pointer relative"}
                onClick={() => window.open(`${BASE_URL}/${language}/news/search?value=${item.hashTags[0]}`, "_blank")}
              >
                <Typography.Text>{item.title}</Typography.Text>
                {item.symbols?.map((symbol: { symbol: string; price: number; change: number }, idx: number) => (
                  <React.Fragment key={idx}>
                    <Typography.Text strong>
                      {symbol.symbol}: {formatThousands(symbol.price, currentCurrency)}
                    </Typography.Text>
                    <PriceChange price={symbol.change} />
                  </React.Fragment>
                ))}
                <Typography.Text
                  className={classNames(
                    "text-sm",
                    item.sentiment === "positive" && "text-green-500",
                    item.sentiment === "negative" && "text-red-500",
                    item.sentiment === "neutral" && "text-gray-500",
                  )}
                >
                  {item.sentiment === "positive" && "↑ Positive"}
                  {item.sentiment === "negative" && "↓ Negative"}
                  {item.sentiment === "neutral" && "- Neutral"}
                </Typography.Text>
                {index !== array.length - 1 && (
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 h-4 w-[1px] bg-gray-300" />
                )}
              </div>
            ))}
            {/* Duplicate set for seamless scrolling */}
            {data?.data?.result?.map((item: FeedItem, index: number, array: FeedItem[]) => (
              <div
                key={`${item.id}-2`}
                className={"inline-flex items-center gap-1 rounded-md px-3 py-1 mx-1 cursor-pointer relative"}
                onClick={() => window.open(`${BASE_URL}/${language}/news/search?value=${item.hashTags[0]}`, "_blank")}
              >
                <Typography.Text>{item.title}</Typography.Text>
                {item.symbols?.map((symbol: { symbol: string; price: number; change: number }, idx: number) => (
                  <React.Fragment key={idx}>
                    <Typography.Text>
                      {symbol.symbol}: {formatThousands(symbol.price, currentCurrency)}
                    </Typography.Text>
                    (<PriceChange price={symbol.change} />)
                  </React.Fragment>
                ))}
                <Typography.Text
                  className={classNames(
                    "text-sm",
                    item.sentiment === "positive" && "text-green-500",
                    item.sentiment === "negative" && "text-red-500",
                    item.sentiment === "neutral" && "text-gray-500",
                  )}
                >
                  {item.sentiment === "positive" && "↑ Positive"}
                  {item.sentiment === "negative" && "↓ Negative"}
                  {item.sentiment === "neutral" && "- Neutral"}
                </Typography.Text>
                {index !== array.length - 1 && (
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 h-4 w-[1px] bg-gray-300" />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      <style jsx>{`
        @keyframes marquee {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }

        .marquee {
          animation: marquee 500s linear infinite;
        }

        .marquee:hover {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  );
}

export default React.memo(FeedCarousel);

